/**
 * EnayaHub Native Bridge
 * ----------------------------------------------------------------------------
 * Single import surface that wraps every Capacitor plugin we use, with safe
 * **graceful fallbacks for the web** so the same component code runs in:
 *
 *   • Mobile Safari / Chrome (PWA)         → falls back to Web Share API,
 *                                              webpush, browser geolocation, …
 *   • iOS native (Capacitor + Xcode build) → native FaceID, APNs, share sheet
 *   • Android native (Capacitor + AS)      → fingerprint, FCM, share sheet
 *
 * Why a single bridge: every Capacitor plugin throws if called on the web
 * platform (it's just not registered). Wrapping all calls behind feature-
 * detected helpers means components don't need `if (Capacitor.isNativePlatform)`
 * sprinkled everywhere.
 */
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Share as CapShare } from '@capacitor/share';
import { Device as CapDevice } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { Browser as CapBrowser } from '@capacitor/browser';
import { PushNotifications } from '@capacitor/push-notifications';
import { BiometricAuth, BiometryError } from '@aparajita/capacitor-biometric-auth';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Platform detection ─────────────────────────────────────────────────────
export const isNative = () => Capacitor.isNativePlatform();
export const isIOS = () => Capacitor.getPlatform() === 'ios';
export const isAndroid = () => Capacitor.getPlatform() === 'android';
export const isWeb = () => Capacitor.getPlatform() === 'web';

// ── Storage (secure on native, localStorage on web) ────────────────────────
// We store the session token here so biometric login can re-issue it without a
// password round-trip. On native this lives in iOS Keychain / Android KeyStore.
export const secureStorage = {
  async set(key, value) {
    if (isNative()) await Preferences.set({ key, value: String(value) });
    else localStorage.setItem(key, String(value));
  },
  async get(key) {
    if (isNative()) return (await Preferences.get({ key })).value;
    return localStorage.getItem(key);
  },
  async remove(key) {
    if (isNative()) await Preferences.remove({ key });
    else localStorage.removeItem(key);
  },
};

// ── Native share ───────────────────────────────────────────────────────────
/**
 * Open the platform share sheet (iOS/Android native, or Web Share API on
 * mobile browsers). Returns true on success, false if the user cancelled or
 * the platform doesn't support sharing.
 */
export async function shareContent({ title, text, url, files }) {
  try {
    if (isNative()) {
      await CapShare.share({ title, text, url, files });
      return true;
    }
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title, text, url });
      return true;
    }
    // Final fallback: copy URL to clipboard so the user can paste anywhere.
    if (url && navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch (e) {
    if (e && (e.name === 'AbortError' || /cancel/i.test(e.message || ''))) return false;
    console.warn('shareContent failed:', e);
  }
  return false;
}

// ── External browser ──────────────────────────────────────────────────────
/**
 * Open a URL in the system browser (in-app browser on native to keep the
 * session alive, regular tab on web).
 */
export async function openExternalUrl(url) {
  if (!url) return;
  if (isNative()) {
    await CapBrowser.open({ url, presentationStyle: 'popover' });
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

// ── Push notifications (FCM / APNs) ─────────────────────────────────────────
/**
 * Request permission + register the device, then POST the FCM/APNs token to
 * `/api/devices/register` so the backend can target it. Idempotent.
 *
 * On web this is a no-op — the existing VAPID web-push flow handles browsers.
 */
export async function registerPushNotifications(sessionToken) {
  if (!isNative()) return { ok: false, reason: 'not_native' };

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return { ok: false, reason: 'denied' };

  await PushNotifications.register();

  return new Promise((resolve) => {
    // Token registration may fire async after `register()`.
    PushNotifications.addListener('registration', async (token) => {
      try {
        const info = await CapDevice.getInfo();
        const res = await fetch(`${BACKEND_URL}/api/devices/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
          },
          body: JSON.stringify({
            token: token.value,
            platform: info.platform, // 'ios' | 'android'
            model: info.model,
            os_version: info.osVersion,
            app_version: info.appVersion || '1.0.0',
          }),
        });
        resolve({ ok: res.ok, token: token.value });
      } catch (e) {
        console.warn('Failed to register device token:', e);
        resolve({ ok: false, reason: 'backend_error' });
      }
    });

    PushNotifications.addListener('registrationError', (e) => {
      console.warn('Push registration error:', e);
      resolve({ ok: false, reason: 'registration_error' });
    });
  });
}

// ── Biometric login ────────────────────────────────────────────────────────
/**
 * Check whether the device offers any form of biometric authentication
 * (FaceID, TouchID, Android fingerprint / face). Returns an object the UI
 * can use to decide whether to show the "Sign in with FaceID" button.
 */
export async function getBiometricCapability() {
  if (!isNative()) return { available: false, reason: 'web' };
  try {
    const info = await BiometricAuth.checkBiometry();
    return {
      available: info.isAvailable,
      biometryType: info.biometryType,    // 'faceId' | 'touchId' | 'fingerprint' | ...
      reason: info.reason,
    };
  } catch (e) {
    return { available: false, reason: String(e && e.message) };
  }
}

/**
 * Prompt for biometric verification. Resolves true on success, false on
 * cancel/failure. Does NOT itself unlock the session — the caller decides
 * what to do (e.g. read sessionToken from secureStorage and call /me).
 */
export async function authenticateWithBiometrics(reason = 'Sign in to EnayaHub') {
  if (!isNative()) return false;
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Cancel',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Use Passcode',
      androidTitle: 'EnayaHub',
      androidSubtitle: reason,
      androidConfirmationRequired: false,
    });
    return true;
  } catch (e) {
    if (e instanceof BiometryError) {
      // user cancelled, lockout, etc. — silent failure is the right UX.
      return false;
    }
    return false;
  }
}

// ── Apple Sign-In (iOS mandatory if any social login is offered) ──────────
/**
 * Trigger Apple Sign-In on iOS / Safari. Returns the identity token the
 * backend needs to verify against Apple's JWKS.
 */
export async function signInWithApple() {
  if (!isNative() && !isIOS()) {
    // Apple Sign-In also works on the web via Apple JS SDK, but that's a
    // separate integration. For v1 we restrict to native iOS.
    return { ok: false, reason: 'unsupported_platform' };
  }
  try {
    const result = await SignInWithApple.authorize({
      clientId: 'net.enayahub.app',
      redirectURI: 'https://enayahub.net/auth/apple/callback',
      scopes: 'email name',
      state: Math.random().toString(36).slice(2),
    });
    return {
      ok: true,
      identityToken: result.response.identityToken,
      authorizationCode: result.response.authorizationCode,
      email: result.response.email,
      givenName: result.response.givenName,
      familyName: result.response.familyName,
    };
  } catch (e) {
    return { ok: false, reason: String(e && e.message) };
  }
}

// ── Deep links ─────────────────────────────────────────────────────────────
/**
 * Subscribe to Universal Link / App Link openings. Whenever the user taps an
 * https://enayahub.net/... link in another app and our app handles it, this
 * callback fires with the full URL. We translate it into an internal
 * react-router navigation.
 */
export function onDeepLink(navigate) {
  if (!isNative()) return () => {};
  const listener = CapApp.addListener('appUrlOpen', (event) => {
    try {
      const u = new URL(event.url);
      // Strip the host — keep only the in-app path (`/booking/manage/...` etc.)
      const inAppPath = u.pathname + u.search + u.hash;
      if (inAppPath) navigate(inAppPath);
    } catch (err) {
      console.warn('Failed to handle deep link:', err);
    }
  });
  return () => listener.then((h) => h.remove());
}

// ── App readiness ─────────────────────────────────────────────────────────
/**
 * Useful for splash-screen handoff or analytics. Returns app info on native.
 */
export async function getNativeAppInfo() {
  if (!isNative()) return null;
  try {
    return await CapApp.getInfo();
  } catch {
    return null;
  }
}
